import type { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import type { ProjectJob } from '../api/types';
import {
  STATS_RANGE_OPTIONS,
  type StatsRange,
} from '../lib/job-range';
import { formatFaNumber } from '../lib/utils';
import { IndexerKpiCard, type IndexerKpiTone } from './IndexerKpiCard';
import { StatsCardHelpTip } from './StatsCardsHelpTip';

/** سقف روزانه Indexing API گوگل (QPD). */
export const GOOGLE_DAILY_QUOTA = 200;

/** سقف روزانه URL Inspection به‌ازای هر پروژه (QPD). */
export const INSPECT_DAILY_QUOTA = 2000;

export function summarizeJobs(jobs: ProjectJob[]) {
  return {
    total: jobs.length,
    requestSuccess: jobs.filter((job) => job.state === 'completed').length,
    requestFailed: jobs.filter(
      (job) =>
        job.state === 'failed' ||
        job.state === 'partial' ||
        job.state === 'cancelled',
    ).length,
    urlSuccess: jobs.reduce((sum, job) => sum + job.successCount, 0),
    urlFailed: jobs.reduce((sum, job) => sum + job.failedCount, 0),
  };
}

type CardTone = IndexerKpiTone;

export function StatsCards({
  jobs,
  rangeDays,
  onRangeChange,
  dailyQuota,
  nonGoogleLabel,
  showRangeSelect = true,
}: {
  jobs: ProjectJob[];
  rangeDays: StatsRange;
  onRangeChange: (days: StatsRange) => void;
  dailyQuota?: {
    used: number;
    limit: number;
    label?: string;
    hint?: string;
    help?: ReactNode;
  };
  nonGoogleLabel?: string;
  /** When false, range dropdown is rendered elsewhere (e.g. tab row). */
  showRangeSelect?: boolean;
}) {
  const stats = summarizeJobs(jobs);
  const items: {
    value: string;
    label: string;
    hint?: string;
    help?: ReactNode;
    tone: CardTone;
  }[] = [];

  if (dailyQuota) {
    const ratio = dailyQuota.used / dailyQuota.limit;
    items.push({
      value: formatFaNumber(dailyQuota.used),
      label: dailyQuota.label ?? 'مصرف امروز',
      hint: dailyQuota.hint,
      help: dailyQuota.help,
      tone: ratio >= 1 ? 'danger' : ratio >= 0.9 ? 'warning' : 'primary',
    });
  }

  if (nonGoogleLabel) {
    items.push({
      value: '—',
      label: `${nonGoogleLabel} · بدون سقف ۲۰۰`,
      tone: 'neutral',
    });
  }

  items.push(
    {
      value: formatFaNumber(stats.total),
      label: 'درخواست',
      help: <StatsCardHelpTip card="request" />,
      tone: 'neutral',
    },
    {
      value: formatFaNumber(stats.requestSuccess),
      label: 'درخواست موفق',
      help: <StatsCardHelpTip card="requestSuccess" />,
      tone: 'success',
    },
    {
      value: formatFaNumber(stats.requestFailed),
      label: 'درخواست ناموفق',
      help: <StatsCardHelpTip card="requestFailed" />,
      tone: 'danger',
    },
    {
      value: formatFaNumber(stats.urlSuccess),
      label: 'آدرس موفق',
      help: <StatsCardHelpTip card="urlSuccess" />,
      tone: 'success',
    },
    {
      value: formatFaNumber(stats.urlFailed),
      label: 'آدرس ناموفق',
      help: <StatsCardHelpTip card="urlFailed" />,
      tone: 'danger',
    },
  );

  return (
    <div className="mb-0">
      {showRangeSelect ? (
        <div className="d-flex justify-content-start mb-3">
          <Form.Select
            size="sm"
            className="w-auto"
            value={rangeDays}
            onChange={(e) => onRangeChange(Number(e.target.value) as StatsRange)}
            aria-label="بازه زمانی آمار"
          >
            {STATS_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>
      ) : null}
      <div
        className={`overview-kpi-grid${
          items.length === 5
            ? ' overview-kpi-grid--5'
            : items.length >= 7
              ? ' overview-kpi-grid--auto'
              : ''
        }`}
      >
        {items.map((item, index) => (
          <IndexerKpiCard key={item.label} tone={item.tone} imageIndex={index}>
            <div className="overview-kpi-value">
              <span>{item.value}</span>
            </div>
            {item.label && (
              <div className="text-secondary small d-inline-flex align-items-center gap-1">
                <span>{item.label}</span>
                {item.help}
              </div>
            )}
            {!item.label && item.help ? (
              <div className="fs--1">{item.help}</div>
            ) : null}
            {item.hint && (
              <div className="overview-kpi-hint">{item.hint}</div>
            )}
          </IndexerKpiCard>
        ))}
      </div>
    </div>
  );
}
